import { useCallback } from 'react'
import { Code, ConnectError } from '@connectrpc/connect'
import { toast } from 'sonner'

import { useSendQuote } from '@/stores/quotes.store'
import { getErrorMessage, mutationErrorHandler, reportError } from '@/lib/error-reporting'
import {
  QuoteSendRecipientStatusEnum,
  type QuoteEmailOverride,
  type QuoteSendRecipientResult,
} from '@/gen/cortado/v1/cortado_pb'

export interface QuoteSendInput {
  quoteId: string
  additionalRecipientEmails?: string[]
  emailOverride?: QuoteEmailOverride
}

interface UseQuoteSendOptions {
  /** True when the quote is still a DRAFT, which changes the success copy. */
  isDraft?: boolean
  onSuccess?: () => void
}

function describeShortfall(results: QuoteSendRecipientResult[]): string | null {
  const failed = results.filter(
    (r) => r.status !== QuoteSendRecipientStatusEnum.QUOTE_SEND_RECIPIENT_STATUS_SENT
  )
  if (failed.length === 0) return null

  const suppressed = failed.filter(
    (r) => r.status === QuoteSendRecipientStatusEnum.QUOTE_SEND_RECIPIENT_STATUS_SUPPRESSED
  )
  const invalid = failed.filter(
    (r) => r.status === QuoteSendRecipientStatusEnum.QUOTE_SEND_RECIPIENT_STATUS_INVALID_EMAIL
  )
  const parts: string[] = []
  if (invalid.length > 0) parts.push(`${invalid.map((r) => r.email).join(', ')} — invalid address`)
  if (suppressed.length > 0) {
    parts.push(`${suppressed.map((r) => r.email).join(', ')} — unsubscribed or bounced`)
  }
  const other = failed.length - invalid.length - suppressed.length
  if (other > 0) parts.push(`${other} ${other === 1 ? 'recipient' : 'recipients'} failed`)
  return parts.join('; ')
}

/**
 * useQuoteSend is the single send path behind every "send this quote" surface.
 *
 * Consolidating it removes the older split where the contact received the
 * branded Quote Sent email and everyone else got a plainer
 * SharePublicInquiryByEmail share — one backend call now mails identical copy to
 * every recipient, each with their own attributed portal link, so this hook no
 * longer needs a second mutation or the partial-success bookkeeping that came
 * with it.
 *
 * Returns true when the quote reached its primary contact. An additional
 * recipient failing is a warning, not a failure: the client got their quote.
 */
export function useQuoteSend({ isDraft = false, onSuccess }: UseQuoteSendOptions = {}) {
  const { sendQuote, isLoading } = useSendQuote()

  const send = useCallback(
    async ({ quoteId, additionalRecipientEmails = [], emailOverride }: QuoteSendInput) => {
      try {
        const response = await sendQuote({
          id: quoteId,
          additionalRecipientEmails,
          emailOverride,
        })

        const sent = response.emailsSent || 0
        const label = sent === 1 ? 'recipient' : 'recipients'
        const shortfall = describeShortfall(response.recipientResults ?? [])

        if (shortfall) {
          toast.warning(`Quote sent to ${sent} ${label}`, { description: shortfall })
        } else {
          toast.success(
            isDraft
              ? `Quote is live and the portal link was sent to ${sent} ${label}`
              : `Portal link sent to ${sent} ${label}`
          )
        }

        onSuccess?.()
        return true
      } catch (error) {
        // A rejected per-send override is the author's own draft, and the
        // server names the offending field. Surfacing that verbatim beats the
        // generic copy, and it is not a bug worth a Sentry event.
        if (error instanceof ConnectError && error.code === Code.InvalidArgument) {
          toast.error("This email can't be sent yet", { description: error.rawMessage })
          return false
        }

        // gmail_token_expired and stripe_not_connected are typed reasons with
        // their own titles and reconnect actions in REASON_MAP, so the shared
        // handler already renders both correctly.
        mutationErrorHandler({ feature: 'quote_send' })(error)
        if (!getErrorMessage(error).expected) {
          reportError(error, { feature: 'quote_send', kind: 'mutation' })
        }
        return false
      }
    },
    [sendQuote, isDraft, onSuccess]
  )

  return { sendQuote: send, isSending: isLoading }
}
